import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingBag, LogOut, Plus, Pencil, Trash2,
  Upload, X, Loader2, IndianRupee, ClipboardList, Boxes, Tag, Image as ImageIcon, FileText, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, inr, formatApiError } from "../../lib/api";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { BRAND } from "../../lib/constants";

const STATUS_OPTIONS = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-[#C5A059]/15 text-[#9c7a2e]",
  confirmed: "bg-[#1F4D2E]/10 text-[#1F4D2E]",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const inputCls = "w-full rounded-xl border border-[#E2DAD0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]";

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

const emptyProduct = {
  name: "", description: "", category: "", price_original: "",
  price_discounted: "", stock: "", image_url: "", bestseller: false, rating: 4.8, reviews_count: 0,
};

const ProductForm = ({ initial, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...emptyProduct, category: categories[0]?.id || "", ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price_original: Number(form.price_original),
      price_discounted: Number(form.price_discounted),
      stock: Number(form.stock), rating: Number(form.rating), reviews_count: Number(form.reviews_count),
    };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(initial?.id ? "Product updated" : "Product added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg my-8" data-testid="product-form-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2DAD0]">
          <h3 className="font-serif text-xl font-bold text-[#1C2B20]">{initial?.id ? "Edit Product" : "Add Product"}</h3>
          <button onClick={onClose} data-testid="product-form-close"><X size={22} className="text-[#5A6B5D]" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Product Image</label>
            <div className="flex items-center gap-4">
              {form.image_url ? (
                <img src={resolveImg(form.image_url)} alt="preview" className="h-20 w-20 rounded-xl object-cover border border-[#E2DAD0]" />
              ) : (
                <div className="h-20 w-20 rounded-xl bg-[#F5F0E6] flex items-center justify-center text-[#8A9A8D]"><Package size={24} /></div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-2 border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-4 py-2 text-sm font-medium hover:bg-[#F5F0E6]">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="product-image-upload" />
              </label>
            </div>
            <input name="image_url" value={form.image_url} onChange={change} placeholder="or paste image URL" className={`${inputCls} mt-3`} data-testid="product-image-url" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input name="name" value={form.name} onChange={change} required className={inputCls} data-testid="product-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={change} rows={3} className={inputCls} data-testid="product-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select name="category" value={form.category} onChange={change} className={inputCls} data-testid="product-category-select">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Stock</label>
              <input name="stock" type="number" value={form.stock} onChange={change} required className={inputCls} data-testid="product-stock-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Original Price (₹)</label>
              <input name="price_original" type="number" value={form.price_original} onChange={change} required className={inputCls} data-testid="product-price-original" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Discounted Price (₹)</label>
              <input name="price_discounted" type="number" value={form.price_discounted} onChange={change} required className={inputCls} data-testid="product-price-discounted" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rating</label>
              <input name="rating" type="number" step="0.1" max="5" value={form.rating} onChange={change} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reviews Count</label>
              <input name="reviews_count" type="number" value={form.reviews_count} onChange={change} className={inputCls} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input name="bestseller" type="checkbox" checked={form.bestseller} onChange={change} className="accent-[#1F4D2E] h-4 w-4" data-testid="product-bestseller-checkbox" />
            Mark as Bestseller
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E2DAD0] rounded-full py-2.5 text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1F4D2E] hover:bg-[#163A22] text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="product-save-btn">
              {saving && <Loader2 size={16} className="animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CategoryForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", image_url: "", order: 0, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, image_url: form.image_url, order: Number(form.order) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/categories/${initial.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(initial?.id ? "Category updated" : "Category added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md my-8" data-testid="category-form-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2DAD0]">
          <h3 className="font-serif text-xl font-bold text-[#1C2B20]">{initial?.id ? "Edit Category" : "Add Category"}</h3>
          <button onClick={onClose} data-testid="category-form-close"><X size={22} className="text-[#5A6B5D]" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Category Image</label>
            <div className="flex items-center gap-4">
              {form.image_url ? (
                <img src={resolveImg(form.image_url)} alt="preview" className="h-20 w-20 rounded-full object-cover border border-[#E2DAD0]" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-[#F5F0E6] flex items-center justify-center text-[#8A9A8D]"><Tag size={24} /></div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-2 border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-4 py-2 text-sm font-medium hover:bg-[#F5F0E6]">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="category-image-upload" />
              </label>
            </div>
            <input name="image_url" value={form.image_url} onChange={change} placeholder="or paste image URL" className={`${inputCls} mt-3`} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input name="name" value={form.name} onChange={change} required className={inputCls} data-testid="category-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Display Order</label>
            <input name="order" type="number" value={form.order} onChange={change} className={inputCls} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E2DAD0] rounded-full py-2.5 text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1F4D2E] hover:bg-[#163A22] text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="category-save-btn">
              {saving && <Loader2 size={16} className="animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#E2DAD0] p-5 flex items-center gap-4">
    <div className="h-12 w-12 rounded-xl bg-[#1F4D2E]/10 flex items-center justify-center text-[#1F4D2E]"><Icon size={22} /></div>
    <div>
      <p className="text-sm text-[#8A9A8D]">{label}</p>
      <p className="text-xl font-bold text-[#1C2B20]">{value}</p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { admin, logout } = useAdminAuth();
  const { refresh: refreshSite } = useSiteSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteAssets, setSiteAssets] = useState([]);
  const [pages, setPages] = useState([]);
  const [contact, setContact] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/site-assets").then((r) => setSiteAssets(r.data)).catch(() => {});
    api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
    api.get("/contact-info").then((r) => setContact(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
    else if (admin) load();
  }, [admin, navigate, load]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center text-[#8A9A8D]">Loading...</div>;
  if (!admin) return null;

  const catName = (id) => categories.find((c) => c.id === id)?.name || id;
  const logoAsset = siteAssets.find((a) => a.type === "logo");
  const banners = siteAssets.filter((a) => a.type === "banner");

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category? Products in it will remain but become unfiltered.")) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changeStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success("Status updated");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadImage(file);
      await api.post("/admin/site-assets", { type: "logo", image_url: url, label: "Brand Logo" });
      toast.success("Logo updated");
      load(); refreshSite();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingLogo(false); }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const url = await uploadImage(file);
      await api.post("/admin/site-assets", { type: "banner", image_url: url, label: file.name });
      toast.success("Banner added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingBanner(false); }
  };

  const deleteAsset = async (id) => {
    try { await api.delete(`/admin/site-assets/${id}`); toast.success("Image removed"); load(); refreshSite(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePage = async (slug, title, content) => {
    try { await api.put(`/admin/pages/${slug}`, { title, content }); toast.success("Page saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveContact = async (data) => {
    try { await api.put("/admin/contact-info", data); toast.success("Contact info saved"); load(); refreshSite(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Tag },
    { id: "site-images", label: "Site Images", icon: ImageIcon },
    { id: "pages", label: "Pages", icon: FileText },
    { id: "contact", label: "Get in Touch", icon: Phone },
    { id: "orders", label: "Orders", icon: ShoppingBag },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E6] flex">
      <aside className="w-20 lg:w-64 bg-[#1F4D2E] text-white flex flex-col shrink-0">
        <div className="p-4 lg:p-6 flex items-center gap-3 border-b border-white/10">
          <img src={resolveImg(logoAsset?.image_url || BRAND.logo)} alt="logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C5A059] bg-white" />
          <span className="font-serif font-bold hidden lg:block">Aarmira Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors ${tab === n.id ? "bg-white/15 text-white" : "text-[#B7C4B2] hover:bg-white/5"}`}>
              <n.icon size={20} /> <span className="hidden lg:block">{n.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
          className="m-3 flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-[#B7C4B2] hover:bg-white/5">
          <LogOut size={20} /> <span className="hidden lg:block">Logout</span>
        </button>
      </aside>

      <div className="flex-1 overflow-x-hidden">
        <div className="p-5 lg:p-8 max-w-6xl">
          {tab === "dashboard" && (
            <div>
              <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mb-6">Dashboard</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={Boxes} label="Products" value={stats?.total_products ?? "—"} />
                <StatCard icon={ClipboardList} label="Total Orders" value={stats?.total_orders ?? "—"} />
                <StatCard icon={ShoppingBag} label="Pending Orders" value={stats?.pending_orders ?? "—"} />
                <StatCard icon={IndianRupee} label="Advance Collected" value={stats ? inr(stats.advance_collected) : "—"} />
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6">
                <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-4">Recent Orders</h2>
                {orders.slice(0, 5).map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-3 border-b border-[#E2DAD0] last:border-0">
                    <div>
                      <p className="font-medium text-sm text-[#1C2B20]">{o.customer_name}</p>
                      <p className="text-xs text-[#8A9A8D]">#{o.id.slice(0, 8).toUpperCase()} · {inr(o.subtotal)}</p>
                    </div>
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                  </div>
                ))}
                {orders.length === 0 && <p className="text-sm text-[#8A9A8D]">No orders yet.</p>}
              </div>
            </div>
          )}

          {tab === "products" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif font-bold text-3xl text-[#1C2B20]">Products</h1>
                <button onClick={() => setEditing({})} data-testid="admin-add-product-btn"
                  className="inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full px-5 py-2.5 text-sm">
                  <Plus size={18} /> Add Product
                </button>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DAD0] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5F0E6] text-[#5A6B5D] text-left">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Product</th>
                        <th className="px-4 py-3 font-semibold">Category</th>
                        <th className="px-4 py-3 font-semibold">Price</th>
                        <th className="px-4 py-3 font-semibold">Stock</th>
                        <th className="px-4 py-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => (
                        <tr key={p.id} className="border-t border-[#E2DAD0]" data-testid={`admin-product-row-${p.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <img src={resolveImg(p.image_url)} alt={p.name} className="h-10 w-10 rounded-lg object-cover bg-[#F5F0E6]" />
                              <span className="font-medium text-[#1C2B20] line-clamp-1 max-w-[220px]">{p.name}</span>
                              {p.bestseller && <span className="bg-[#1F4D2E] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">BEST</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#5A6B5D]">{catName(p.category)}</td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-[#1F4D2E]">{inr(p.price_discounted)}</span>
                            <span className="text-[#9E9E9E] line-through ml-2 text-xs">{inr(p.price_original)}</span>
                          </td>
                          <td className="px-4 py-3 text-[#5A6B5D]">{p.stock}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => setEditing(p)} className="p-2 text-[#1F4D2E] hover:bg-[#F5F0E6] rounded-lg" data-testid={`admin-edit-${p.id}`}><Pencil size={16} /></button>
                              <button onClick={() => handleDelete(p.id)} className="p-2 text-[#C86D51] hover:bg-red-50 rounded-lg" data-testid={`admin-delete-${p.id}`}><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "categories" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif font-bold text-3xl text-[#1C2B20]">Categories</h1>
                <button onClick={() => setEditingCat({})} data-testid="admin-add-category-btn"
                  className="inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full px-5 py-2.5 text-sm">
                  <Plus size={18} /> Add Category
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl border border-[#E2DAD0] p-4 flex items-center gap-4" data-testid={`admin-category-${c.id}`}>
                    <img src={resolveImg(c.image_url)} alt={c.name} className="h-16 w-16 rounded-full object-cover bg-[#F5F0E6]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1C2B20]">{c.name}</p>
                      <p className="text-xs text-[#8A9A8D]">order: {c.order}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => setEditingCat(c)} className="p-2 text-[#1F4D2E] hover:bg-[#F5F0E6] rounded-lg" data-testid={`admin-edit-cat-${c.id}`}><Pencil size={16} /></button>
                      <button onClick={() => deleteCategory(c.id)} className="p-2 text-[#C86D51] hover:bg-red-50 rounded-lg" data-testid={`admin-delete-cat-${c.id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "site-images" && (
            <div>
              <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mb-6">Site Images</h1>
              <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6 mb-6">
                <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-4">Website Logo</h2>
                <div className="flex items-center gap-6">
                  <img src={resolveImg(logoAsset?.image_url || BRAND.logo)} alt="logo" className="h-24 w-24 rounded-full object-cover ring-2 ring-[#C5A059] bg-[#F5F0E6]" />
                  <label className="cursor-pointer inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white rounded-full px-5 py-2.5 text-sm font-medium" data-testid="admin-logo-upload-label">
                    {uploadingLogo ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Replace Logo
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="admin-logo-upload" />
                  </label>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-xl font-bold text-[#1C2B20]">Banner & Promo Images</h2>
                  <label className="cursor-pointer inline-flex items-center gap-2 border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#F5F0E6]" data-testid="admin-banner-upload-label">
                    {uploadingBanner ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add Image
                    <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} data-testid="admin-banner-upload" />
                  </label>
                </div>
                {banners.length === 0 ? (
                  <p className="text-sm text-[#8A9A8D]">No banner images yet. Upload promotional images to manage them here.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {banners.map((b) => (
                      <div key={b.id} className="relative group rounded-xl overflow-hidden border border-[#E2DAD0]" data-testid={`admin-banner-${b.id}`}>
                        <img src={resolveImg(b.image_url)} alt={b.label} className="h-36 w-full object-cover" />
                        <button onClick={() => deleteAsset(b.id)} className="absolute top-2 right-2 bg-white/90 text-[#C86D51] rounded-full p-2 hover:bg-white" data-testid={`admin-banner-delete-${b.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "pages" && (
            <div>
              <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mb-6">Content Pages</h1>
              <div className="space-y-5">
                {pages.map((pg) => (
                  <PageEditor key={pg.slug} page={pg} onSave={savePage} />
                ))}
                {pages.length === 0 && <p className="text-sm text-[#8A9A8D]">No pages.</p>}
              </div>
            </div>
          )}

          {tab === "contact" && (
            <div>
              <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mb-6">Get in Touch</h1>
              <p className="text-[#5A6B5D] mb-6 text-sm">
                This contact information is shown in the website footer and powers the floating WhatsApp button.
              </p>
              {contact && <ContactEditor contact={contact} onSave={saveContact} />}
            </div>
          )}

          {tab === "orders" && (
            <div>
              <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mb-6">Orders</h1>
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white rounded-2xl border border-[#E2DAD0] p-5" data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-[#1C2B20]">{o.customer_name} · <span className="font-normal text-[#5A6B5D]">{o.phone}</span></p>
                        <p className="text-xs text-[#8A9A8D] mt-0.5">#{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleString("en-IN")}{o.customer_email ? ` · ${o.customer_email}` : ""}</p>
                        <p className="text-sm text-[#5A6B5D] mt-2 max-w-md">{o.address}{o.city ? `, ${o.city}` : ""}{o.pincode ? ` - ${o.pincode}` : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#1C2B20]">{inr(o.subtotal)}</p>
                        <p className="text-xs text-[#8A9A8D]">Advance {inr(o.advance_amount)} · {o.advance_paid ? "Paid" : "Unpaid"}</p>
                        <p className="text-xs text-[#8A9A8D]">Balance {inr(o.balance_due)} (COD)</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#E2DAD0] pt-4">
                      {o.items.map((it, idx) => (
                        <span key={idx} className="text-xs bg-[#F5F0E6] rounded-full px-3 py-1 text-[#5A6B5D]">{it.name} ×{it.quantity}</span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <span className="text-sm text-[#5A6B5D]">Status:</span>
                      <select value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)} data-testid={`admin-order-status-${o.id}`}
                        className={`text-sm font-semibold rounded-full px-4 py-1.5 capitalize border-0 focus:outline-none focus:ring-2 focus:ring-[#1F4D2E] ${STATUS_COLORS[o.status]}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {orders.length === 0 && <p className="text-sm text-[#8A9A8D]">No orders yet.</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {editing && <ProductForm initial={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {editingCat && <CategoryForm initial={editingCat} onClose={() => setEditingCat(null)} onSaved={() => { setEditingCat(null); load(); refreshSite(); }} />}
    </div>
  );
};

const PageEditor = ({ page, onSave }) => {
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content);
  const [saving, setSaving] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-[#E2DAD0] p-5" data-testid={`admin-page-${page.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono bg-[#F5F0E6] px-2 py-1 rounded text-[#5A6B5D]">/{page.slug}</span>
        <button
          onClick={async () => { setSaving(true); await onSave(page.slug, title, content); setSaving(false); }}
          disabled={saving}
          data-testid={`admin-page-save-${page.slug}`}
          className="inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          {saving && <Loader2 size={14} className="animate-spin" />} Save
        </button>
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} className={`${inputCls} mb-3 font-semibold`} data-testid={`admin-page-title-${page.slug}`} />
      <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={6} className={inputCls} data-testid={`admin-page-content-${page.slug}`} />
    </div>
  );
};

const ContactEditor = ({ contact, onSave }) => {
  const [form, setForm] = useState({
    phone: contact.phone || "", email: contact.email || "",
    whatsapp: contact.whatsapp || "", address: contact.address || "",
  });
  const [saving, setSaving] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const fields = [
    { name: "phone", label: "Phone Number", placeholder: "+91 98765 43210" },
    { name: "whatsapp", label: "WhatsApp Number (with country code)", placeholder: "919355662555" },
    { name: "email", label: "Email Address", placeholder: "care@yourbrand.in" },
  ];
  return (
    <div className="bg-white rounded-2xl border border-[#E2DAD0] p-6 max-w-xl">
      <div className="space-y-4">
        {fields.map((fd) => (
          <div key={fd.name}>
            <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">{fd.label}</label>
            <input
              name={fd.name}
              value={form[fd.name]}
              onChange={change}
              placeholder={fd.placeholder}
              className={inputCls}
              data-testid={`contact-${fd.name}-input`}
            />
          </div>
        ))}
        <div>
          <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">Physical Address</label>
          <textarea
            name="address"
            value={form.address}
            onChange={change}
            rows={3}
            placeholder="Shop / Office address"
            className={inputCls}
            data-testid="contact-address-input"
          />
        </div>
        <p className="text-xs text-[#8A9A8D]">
          Tip: The WhatsApp number should include the country code and no spaces or symbols (e.g. 919355662555) for the chat button to work.
        </p>
        <button
          onClick={async () => { setSaving(true); await onSave(form); setSaving(false); }}
          disabled={saving}
          data-testid="contact-save-btn"
          className="inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60"
        >
          {saving && <Loader2 size={16} className="animate-spin" />} Save Contact Info
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;
